import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero, { HeroCtas } from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import DataTable from '../components/ui/DataTable';
import CardSelect from '../components/ui/CardSelect';
import Notice from '../components/ui/Notice';
import StageList from '../components/ui/StageList';
import Faq from '../components/ui/Faq';
import Button from '../components/ui/Button';
import { serviceSchema, faqSchema, breadcrumbSchema } from '../components/ui/schema';

const SIBLING_LINKS = { 'Property Development': '/property-development', 'Built Environment Investments': '/built-environment-investments', 'Innovation & Ventures': '/innovation-ventures' };

const BuiltEnvironmentInvestments = () => {
  const { t } = useTranslation('cy');
  const meta = t('built.meta', { returnObjects: true });
  const [situation, setSituation] = useState(null);

  const faqItems = t('built.faq.items', { returnObjects: true });

  const schema = [
    serviceSchema('Built Environment Investments', meta.description),
    faqSchema(faqItems),
    breadcrumbSchema([{ label: 'Home', to: '/' }, { label: 'Built Environment Investments', to: '/built-environment-investments' }]),
  ];

  const situations = t('built.situations.items', { returnObjects: true });

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/built-environment-investments" schema={schema} />

      <PageHero
        id="built-hero"
        eyebrow={t('built.hero.eyebrow')}
        title={t('built.hero.h1')}
        lead={t('built.hero.lead')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.pillars.built.label') }]} />}
      >
        <p className="cy-body" style={{ marginTop: 24 }}>{t('built.hero.boundary')}</p>
        <HeroCtas
          primary={t('built.hero.cta')}
          primaryTo="/submit-opportunity?type=business"
          secondary={t('built.hero.seeCriteria')}
          secondaryTo="#built-focus"
        />
        <Notice variant="inline" className="cy-hero__boundary">{t('built.hero.roleNote')}</Notice>
      </PageHero>

      {/* built-focus */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="built-focus">
        <div className="cy-container">
          <SectionHead title={t('built.focus.h2')} lead={t('built.focus.lead')} />
          <div className="cy-grid cy-grid--4">
            {t('built.focus.sectors', { returnObjects: true }).map((s) => (
              <div className="cy-card" key={s.title}>
                <h3 className="cy-card-title">{s.title}</h3>
                <p className="cy-card-text">{s.body}</p>
              </div>
            ))}
          </div>
          <h3 className="cy-card-title" style={{ marginTop: 48, marginBottom: 16 }}>Business characteristics</h3>
          <DataTable
            columns={[
              { key: 'dim', label: 'Dimension' },
              { key: 'what', label: 'What we consider' },
              { key: 'verify', label: 'How we verify' },
            ]}
            rows={t('built.focus.traits', { returnObjects: true })}
          />
          <p className="cy-body" style={{ marginTop: 24 }}>{t('built.focus.note')}</p>
          <p className="cy-body" style={{ marginTop: 24 }}>
            {t('built.focus.gateNote')}{' '}
            <Link className="cy-link" to="/approach#approach-gates">{t('built.focus.gateLink')}</Link>
          </p>
        </div>
      </section>

      {/* built-not-fit */}
      <section className="cy-section cy-zone--doc" id="built-not-fit">
        <div className="cy-container">
          <SectionHead title={t('built.notFit.h2')} lead={t('built.notFit.lead')} />
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {t('built.notFit.items', { returnObjects: true }).map((item) => (
              <li className="cy-principle" key={item.title}>
                <h3 className="cy-principle__title">{item.title}</h3>
                <p className="cy-card-text">{item.body}</p>
              </li>
            ))}
          </ul>
          <Notice variant="compliance" className="cy-notice--inline" style={{ marginTop: 24 }}>
            {t('built.notFit.boundary')} {t('built.notFit.dealBoundary')}
          </Notice>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/#home-pillars">{t('built.notFit.alt')}</Link>
          </p>
        </div>
      </section>

      {/* built-situations */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="built-situations">
        <div className="cy-container">
          <SectionHead title={t('built.situations.h2')} lead={t('built.situations.lead')} />
          <CardSelect
            name="situation"
            columns={2}
            value={situation}
            onChange={setSituation}
            options={situations.map((s) => ({
              value: s.title,
              title: s.title,
              desc: s.what,
              summary: s.boundary,
            }))}
          />
          {situation && (
            <div style={{ marginTop: 24 }}>
              <Button to="/submit-opportunity?type=business" variant="secondary">
                {t('built.situations.continue')}
              </Button>
            </div>
          )}
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('built.situations.note')}</p>
        </div>
      </section>

      {/* built-structures-governance */}
      <section className="cy-section cy-zone--doc" id="built-structures-governance">
        <div className="cy-container">
          <SectionHead title={t('built.governance.h2')} lead={t('built.governance.lead')} />
          <DataTable
            columns={[
              { key: 'theme', label: 'Theme' },
              { key: 'preference', label: 'Typical preference' },
              { key: 'variable', label: 'Case by case' },
            ]}
            rows={t('built.governance.rows', { returnObjects: true })}
          />
          <Notice variant="compliance" style={{ marginTop: 24 }}>{t('built.governance.note')}</Notice>
        </div>
      </section>

      {/* built-value-creation */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="built-value-creation">
        <div className="cy-container">
          <SectionHead eyebrow={t('built.value.eyebrow')} title={t('built.value.h2')} lead={t('built.value.lead')} />
          <DataTable
            columns={[
              { key: 'topic', label: 'Area' },
              { key: 'input', label: 'Input' },
              { key: 'action', label: 'Possible actions' },
              { key: 'measure', label: 'How we measure' },
            ]}
            rows={t('built.value.rows', { returnObjects: true })}
          />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('built.value.note')}</p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/about#about-team">{t('built.value.cta')}</Link>
          </p>
        </div>
      </section>

      {/* built-confidentiality */}
      <section className="cy-section cy-zone--doc" id="built-confidentiality">
        <div className="cy-container">
          <SectionHead title={t('built.confidentiality.h2')} lead={t('built.confidentiality.lead')} />
          <StageList items={t('built.confidentiality.layers', { returnObjects: true })} />
          <p className="cy-body" style={{ marginTop: 24 }}>
            <strong style={{ color: 'var(--cy-color-text-primary)' }}>Not needed in the first round:</strong>{' '}
            {t('built.confidentiality.notFirst')}
          </p>
          <Notice variant="inline" style={{ marginTop: 16 }}>{t('built.confidentiality.note')}</Notice>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/privacy#privacy-disclosure">{t('built.confidentiality.cta')}</Link>
          </p>
        </div>
      </section>

      {/* built-process */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="built-process">
        <div className="cy-container">
          <SectionHead title={t('built.process.h2')} lead={t('built.process.lead')} />
          <StageList items={t('built.process.steps', { returnObjects: true })} />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('built.process.note')}</p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-stages">{t('built.process.cta')}</Link>
          </p>
        </div>
      </section>

      {/* built-faq-cta */}
      <section className="cy-section cy-section--cta" id="built-faq-cta">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('built.faq.h2')} />
          <Faq items={faqItems} idPrefix="built-faq" />
          <div style={{ marginTop: 48, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            <Button to="/submit-opportunity?type=business">{t('built.faq.cta')}</Button>
            <Link className="cy-link" to="/contact?topic=partnership">{t('built.faq.alt')}</Link>
          </div>
          <p className="cy-fineprint" style={{ marginTop: 32 }}>
            <Link className="cy-link" to="/property-development">Property Development</Link>
            {' · '}
            <Link className="cy-link" to="/innovation-ventures">Innovation & Ventures</Link>
          </p>
        </div>
          <p className="cy-fineprint" style={{ marginTop: 32 }}>
            {t('built.faq.siblings', { returnObjects: true }).map((s, i, arr) => (
              <React.Fragment key={s}>
                <Link className="cy-link" to={SIBLING_LINKS[s]}>{s}</Link>
                {i < arr.length - 1 ? ' · ' : ''}
              </React.Fragment>
            ))}
          </p>
      </section>
    </Layout>
  );
};

export default BuiltEnvironmentInvestments;
